// 棚卸しの「あと何を数えていないか」を出すための述語と絞り込み。
//
// 🔴 棚卸しは物理的に「リストを潰し切る」作業なのに、画面は全行を同じ見た目で
// 並べるだけで、残りが何件でどれなのかを一切出していなかった＝倉庫に立っている
// 人が、数え終わった行と未着手の行を目視で追う羽目になっていた。
import type { StocktakeItem } from "../api/stocktakes";
import { parseQuantityInput } from "./quantityInput";
import {
  merchantSafeRecordedProductTitle,
  projectedProductTitle,
} from "./productTitles";

/** 欄に打ち込み中の実数（保存前）。key = shopify_variant_id。 */
export type CountDrafts = Record<string, string>;

/**
 * その商品の実数が入っているか。
 *
 * 🔑 **保存済みかどうかでは判定しない**＝打ち込んだ端から「まだ残っている」と
 * 言われると残件数が使い物にならない。逆に、保存済みでも欄を空にしたら
 * 「やっぱり数えていない」の意思表示なので未入力へ戻す（画面の見た目と一致させる）。
 */
export function isEntered(item: StocktakeItem, drafts: CountDrafts): boolean {
  const draft = drafts[String(item.shopify_variant_id)];
  if (draft !== undefined) return parseQuantityInput(draft).kind === "value";
  return item.counted_qty !== null;
}

/** 実数が入っている商品の件数（残り＝total − これ）。 */
export function countEnteredItems(
  items: StocktakeItem[],
  drafts: CountDrafts,
): number {
  return items.filter((item) => isEntered(item, drafts)).length;
}

export interface StocktakeItemFilter {
  /** 商品名・SKU・Shopify variant ID の部分一致。空＝絞らない。 */
  query: string;
  /** まだ実数を入れていない商品だけに絞る（＝残作業の一覧）。 */
  uncountedOnly: boolean;
}

/**
 * カウント表の絞り込み。
 * 🔴 名前・SKU が null でも variant ID は常に検索対象にする。商品ラベルが欠けた
 * 行も倉庫で一意に探せる一方、検索語があるのに無関係な行は残さない。
 */
export function filterStocktakeItems(
  items: StocktakeItem[],
  { query, uncountedOnly }: StocktakeItemFilter,
  drafts: CountDrafts,
): StocktakeItem[] {
  const needle = query.trim().toLowerCase();
  return items.filter((item) => {
    if (uncountedOnly && isEntered(item, drafts)) return false;
    if (needle === "") return true;
    return `${projectedProductTitle(item) ?? ""} ${merchantSafeRecordedProductTitle(item) ?? ""} ${item.sku ?? ""} ${item.shopify_variant_id}`
      .toLowerCase()
      .includes(needle);
  });
}
